import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { ConstructionLoader } from "@/components/ui/ConstructionLoader";
import { NavProgressBar } from "@/components/ui/NavProgressBar";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { VisitorTracker } from "./components/VisitorTracker";
import { ConstructionCompanySchema } from "@/components/seo/ConstructionCompanySchema";
import { ErrorBoundary } from "@/components/ui/ErrorBoundary";
import api from "@/services/api";
import { PageTransition as PT } from "@/components/ui/PageTransition";

// Admin Imports
import { AdminProtectedRoute } from "@/admin/components/AdminProtectedRoute";
import "@/admin/admin-styles.css";

const Index = lazy(() => import("./pages/Index"));
const Portfolio = lazy(() => import("./pages/Portfolio"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const ProjectDetails = lazy(() => import("./pages/ProjectDetails"));
const NotFound = lazy(() => import("./pages/NotFound"));
const SyncPage = lazy(() => import("./pages/Sync").then((m) => ({ default: m.SyncPage })));

const AdminLoginPage = lazy(() => import("@/admin/pages/Login").then((m) => ({ default: m.LoginPage })));
const AdminDashboard = lazy(() => import("@/admin/pages/Dashboard").then((m) => ({ default: m.Dashboard })));
const AdminProjectsPage = lazy(() => import("@/admin/pages/Projects").then((m) => ({ default: m.ProjectsPage })));
const AdminAccountPage = lazy(() => import("@/admin/pages/Account").then((m) => ({ default: m.AccountPage })));
const AdminChartsPage = lazy(() => import("@/admin/pages/Charts").then((m) => ({ default: m.ChartsPage })));
const AdminEnquiriesPage = lazy(() => import("@/admin/pages/Enquiries").then((m) => ({ default: m.EnquiriesPage })));
const AdminReviewsPage = lazy(() => import("@/admin/pages/Reviews").then((m) => ({ default: m.ReviewsPage })));

// Suspense fallback for lazy route chunks. This is the REAL loading state
// (the chunk genuinely hasn't downloaded yet) as opposed to ConstructionLoader,
// which is a fixed-duration first-visit brand flash. Most navigations won't
// even see this — the chunk is usually already cached — but on a slow
// connection it gives feedback instead of a blank area.
function RouteFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center" role="status" aria-label="Loading page">
      <span className="h-8 w-8 rounded-full border-2 border-orange-500/25 border-t-orange-500 animate-spin" />
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 0,
      refetchOnWindowFocus: true,
    },
  },
});

function AnimatedRoutes() {
  const location = useLocation();

  const withSuspense = (node: JSX.Element) => (
    <Suspense fallback={<RouteFallback />}>
      {node}
    </Suspense>
  );
  
  return (
    // No mode="wait" here on purpose: "wait" fully fades the old page out
    // (to opacity 0) before the new one starts fading in, so there's a gap
    // where nothing but the blank page background is visible — the "white
    // screen" flash. Default mode crossfades both at once instead.
    <AnimatePresence>
      <Routes location={location} key={location.pathname}>
        {/* Public routes */}
        <Route path="/" element={withSuspense(<PT><Index /></PT>)} />
        <Route path="/portfolio" element={withSuspense(<PT><Portfolio /></PT>)} />
        <Route path="/portfolio/:slug" element={withSuspense(<PT><ProjectDetails /></PT>)} />
        <Route path="/about" element={withSuspense(<PT><About /></PT>)} />
        <Route path="/services" element={withSuspense(<PT><Services /></PT>)} />
        <Route path="/contact" element={withSuspense(<PT><Contact /></PT>)} />
        <Route path="/blog" element={withSuspense(<PT><Blog /></PT>)} />
        <Route path="/sync" element={withSuspense(<PT><SyncPage /></PT>)} />
        
        {/* Admin Routes */}
        {/*
          Deliberately NOT wrapped in <PT> (PageTransition). PT's motion.div sets
          will-change/transform, which — per the CSS spec — creates a new containing
          block for any `position: fixed`/`sticky` descendant. The admin Sidebar relies
          on `fixed left-0 top-0` to stay pinned to the viewport; inside <PT> it was
          instead pinning to that animated wrapper, so it scrolled away with the page.
        */}
        <Route path="/astadmin/login" element={withSuspense(<AdminLoginPage />)} />
        <Route path="/astadmin" element={withSuspense(<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>)} />
        <Route path="/astadmin/dashboard" element={withSuspense(<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>)} />
        <Route path="/astadmin/projects" element={withSuspense(<AdminProtectedRoute><AdminProjectsPage /></AdminProtectedRoute>)} />
        <Route path="/astadmin/account" element={withSuspense(<AdminProtectedRoute><AdminAccountPage /></AdminProtectedRoute>)} />
        <Route path="/astadmin/charts" element={withSuspense(<AdminProtectedRoute><AdminChartsPage /></AdminProtectedRoute>)} />
        <Route path="/astadmin/enquiries" element={withSuspense(<AdminProtectedRoute><AdminEnquiriesPage /></AdminProtectedRoute>)} />
        <Route path="/astadmin/reviews" element={withSuspense(<AdminProtectedRoute><AdminReviewsPage /></AdminProtectedRoute>)} />

        <Route path="*" element={withSuspense(<PT><NotFound /></PT>)} />
      </Routes>
    </AnimatePresence>
  );
}

// Full-screen brand loader: only ever shown once, on the very first cold
// paint of the app. Every later in-app navigation gets NavProgressBar instead
// (a thin top bar, no full-screen takeover, no logo/name replay) — repeating
// the full animation on every click reads as friction/gimmick rather than
// polish once you're a few pages into a session.
const FIRST_LOAD_DURATION_MS = 1400;
const NAV_PROGRESS_DURATION_MS = 550;

function RouterShell() {
  const location = useLocation();
  const firstLoad = useRef(true);
  const [showFirstLoader, setShowFirstLoader] = useState(
    () => !location.pathname.startsWith('/astadmin'),
  );
  const [showNavProgress, setShowNavProgress] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;

    const isAdminRoute = location.pathname.startsWith('/astadmin');
    if (isAdminRoute) {
      return;
    }

    const pagePath = `${location.pathname}${location.search}`;
    const gtag = (window as Window & { gtag?: (...args: unknown[]) => void }).gtag;

    if (typeof gtag === "function") {
      gtag("event", "page_view", { page_path: pagePath });
    }

    api.post('events/', {
      event_type: 'page_view',
      path: pagePath,
      label: document.title,
    }).catch(() => undefined);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const isAdminRoute = location.pathname.startsWith('/astadmin');
    if (isAdminRoute) {
      setShowFirstLoader(false);
      setShowNavProgress(false);
      return;
    }

    if (firstLoad.current) {
      setShowFirstLoader(true);
      const timer = window.setTimeout(() => {
        setShowFirstLoader(false);
        firstLoad.current = false;
      }, FIRST_LOAD_DURATION_MS);
      return () => window.clearTimeout(timer);
    }

    setShowNavProgress(true);
    const timer = window.setTimeout(() => setShowNavProgress(false), NAV_PROGRESS_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [location.pathname]);

  return (
    <>
      <ConstructionLoader isVisible={showFirstLoader} durationMs={FIRST_LOAD_DURATION_MS} />
      <NavProgressBar isVisible={showNavProgress} />
      <ConstructionCompanySchema />
      <AnimatedRoutes />
    </>
  );
}

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <VisitorTracker />
          <BrowserRouter
            future={{
              v7_relativeSplatPath: true,
              v7_startTransition: true,
            }}
          >
            <RouterShell />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
